"use client";

import markdownToHtml from "@/utils/htmlFromatter";
import { RefObject, useEffect, useRef, useState } from "react";

/* =====================
   Types
===================== */

interface MarkdownImage {
  filename: string;
  url: string;
  title?: string;
}

interface ItineraryItem {
  title: string;
  description: string;
}

interface MapRef {
  flyToStop?: (id: number) => void;
}

interface AccordionProps {
  itinerary: ItineraryItem[];
  title?: string;
  images?: MarkdownImage[];
  showItineraryDisclaimer?: boolean;
  focusedId?: number | null;
  setFocusedId?: (id: number) => void;
  mapRef?: RefObject<MapRef | null>;
}

/* =====================
   Component
===================== */

export default function Accordion({
  itinerary,
  title,
  images = [],
  showItineraryDisclaimer = true,
  focusedId,
  setFocusedId,
  mapRef,
}: AccordionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const contentRefs = useRef<Array<HTMLDivElement | null>>([]);

  const toggleAccordion = (index: number) => {
    const alreadyOpen = openIndex === index;

    if (alreadyOpen) {
      collapse(index);
      setOpenIndex(null);
    } else {
      if (openIndex !== null) collapse(openIndex);
      expand(index);
      setOpenIndex(index);
    }

    if (setFocusedId) setFocusedId(index + 1);
    if (mapRef?.current?.flyToStop) {
      mapRef.current.flyToStop(index + 1);
    }
  };

  const expand = (index: number) => {
    const el = contentRefs.current[index];
    if (!el) return;

    el.style.removeProperty("display");
    el.style.height = "auto";
    const height = `${el.scrollHeight}px`;

    el.style.height = "0px";

    requestAnimationFrame(() => {
      el.style.transition = "height 300ms ease";
      el.style.height = height;
    });
  };

  const collapse = (index: number) => {
    const el = contentRefs.current[index];
    if (!el) return;

    const height = `${el.scrollHeight}px`;
    el.style.height = height;

    requestAnimationFrame(() => {
      el.style.transition = "height 300ms ease";
      el.style.height = "0px";
    });

    el.addEventListener(
      "transitionend",
      () => {
        if (el.style.height === "0px") {
          el.style.display = "none";
        }
      },
      { once: true },
    );
  };

  useEffect(() => {
    itinerary.forEach((_, index) => {
      const el = contentRefs.current[index];
      if (index !== openIndex && el) {
        el.style.display = "none";
        el.style.height = "0px";
      }
    });
  }, [itinerary, openIndex]);

  useEffect(() => {
    if (focusedId == null) return;

    const index = focusedId - 1;

    if (index !== openIndex) {
      if (openIndex !== null) collapse(openIndex);
      expand(index);
      setOpenIndex(index);
    }

    requestAnimationFrame(() => {
      if (mapRef?.current?.flyToStop) {
        mapRef.current.flyToStop(focusedId);
      }
    });
  }, [focusedId]);

  useEffect(() => {
    if (focusedId == null) return;

    const index = focusedId - 1;

    // open item if needed
    if (index !== openIndex) {
      if (openIndex !== null) collapse(openIndex);
      expand(index);
      setOpenIndex(index);
    }

    // always trigger map movement
    requestAnimationFrame(() => {
      if (mapRef?.current?.flyToStop) {
        mapRef.current.flyToStop(focusedId);
      }
    });
  }, [focusedId]);

  return (
    <>
      <h3 className='font-kalam font-bold mt-14 text-4xl md:text-5xl text-center mb-10'>
        {title}
      </h3>

      {showItineraryDisclaimer && (
        <p className='text-[16px] font-kalam w-full mb-4 text-center'>
          Itinerár sa môže z nepredvídateľných dôvodov, ako je napríklad počasie
          meniť, no urobíme všetko preto, aby to bolo tip top!
        </p>
      )}

      <div className='w-full px-0 md:px-8 lg:px-16'>
        {itinerary.map((item, index) => {
          const isActive = openIndex === index;

          return (
            <div key={index} className='border-b border-gray-300'>
              <div className='transition-colors'>
                <button
                  className='w-full flex justify-between md:items-center py-4 px-3 text-left focus:outline-none group'
                  onClick={() => toggleAccordion(index)}
                >
                  <div className='flex md:items-center gap-4'>
                    <div
                      className={`w-10 h-10 shrink-0 flex items-center justify-center rounded-full text-white text-lg font-kalam font-bold transition-colors ${
                        isActive
                          ? "bg-[#00638A]"
                          : "bg-[#A3A3A3] group-hover:bg-[#00638A]"
                      }`}
                    >
                      {index + 1}.
                    </div>

                    <span
                      className={`text-xl w-[80%] md:pt-0 pt-1 md:w-full md:text-2xl font-kalam font-bold transition-colors ${
                        isActive
                          ? "text-[#00638A]"
                          : "text-black group-hover:text-[#00638A]"
                      }`}
                    >
                      {item.title}
                    </span>
                  </div>

                  <div
                    className={`w-10 h-10 shrink-0 flex items-center justify-center rounded-full transition-colors ${
                      isActive
                        ? "bg-[#00638A]"
                        : "bg-[#A3A3A3] group-hover:bg-[#00638A]"
                    }`}
                  >
                    <svg
                      className={`w-6 h-6 shrink-0 transform transition-transform duration-300 ${
                        isActive ? "rotate-360 mt-1" : "rotate-270 ml-1"
                      }`}
                      xmlns='http://www.w3.org/2000/svg'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='white'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <polyline points='6 9 12 15 18 9' />
                    </svg>
                  </div>
                </button>
              </div>

              {isActive && (
                <div
                  ref={(el) => {
                    contentRefs.current[index] = el;
                  }}
                  className='md:px-3 w-[90%] mb-3 mx-auto md:mr-0 md:ml-13 text-left text-sm md:text-base text-gray-600 whitespace-pre-line transition-opacity duration-200 opacity-100'
                >
                  {markdownToHtml(item.description, images)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
